/**
 * @file Component to view the capacity
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef } from '@angular/core';
import * as moment from 'moment';
import { AlertsService } from '../../core/alerts.service';
import { EventsService } from '../../core/event.service';
import { LocalisationService } from '../../core/localisation.service';
import { ForecastService } from '../forecast.service';
import { PluginViewForecastBase, PLANITEMS, PROGNOSES, CORRECTIONS, SHRINKAGES } from '../plugin-view-forecast/plugin-view-forecast.base';
import { SettingsService } from '../../settings.service';
import { GridService } from '../../grids/grid.service';
/**
 * Class to show the grid capacity
 */
var PluginViewCapacityComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewCapacityComponent, _super);
    /**
     * Creates an instance of PluginViewCapacityComponent.
     * @param {LocalisationService} localisationService The localisation service.
     * @param {ChangeDetectorRef} changeDetector The change detector is used to update the rendered layout.
     * @param {EventsService} eventsService The events service.
     * @param {SettingsService} settingsService The settings service.
     * @param {ForecastService} forecastService The forecast service is used to retrieve prognoses.
     * @param {AlertsService} alertsService The alerts service.
     * @memberOf PluginViewCapacityComponent
     */
    function PluginViewCapacityComponent(localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) || this;
        /**
         * The names of the queries that are loaded when the plugin view refreshes.
         * @protected
         * @memberOf PluginViewCapacityComponent
         */
        _this.queryNames = [PLANITEMS, PROGNOSES, CORRECTIONS, SHRINKAGES];
        return _this;
    }
    /**
     * Builds the calls chart.
     * @protected
     * @memberOf PluginViewCapacityComponent
     */
    PluginViewCapacityComponent.prototype.build = function () {
        var _this = this;
        // The absolute highest value of all displayed charts.
        var maxValue = 0;
        var pluginBars = [];
        // The service level agreement and maximum waiting time are static.
        var sla = this.selectedFunction['SLA'] / 100;
        var maxWait = moment.duration(this.selectedFunction['MaxWait']);
        this.gridCollections.forEach(function (gridSegment) {
            gridSegment.children.forEach(function (gridRows) {
                gridRows.children.forEach(function (gridRow) {
                    // A grid column is one day on the planboard.
                    gridRow.children.forEach(function (gridColumn) {
                        gridColumn.pluginBars = [];
                        if (_this.queryResults[PROGNOSES].length === 0) {
                            return;
                        }
                        // The correction factor is 1 if there is not correction.
                        var correctionEntity = _this.queryResults[CORRECTIONS].find(function (c) { return gridColumn.meta.start.startOf('day').isSame(c['Date']); });
                        var correction = correctionEntity ? correctionEntity['Value'] : 1;
                        // The dimensions are the start, finish and derived width of the one bar. In this case the first.
                        var dimensions = _this.getBarDimensions(gridColumn);
                        var _loop_1 = function () {
                            // Collect all prognoses that fall within the dimensions.
                            // Prognoses may not overlap partially with a bar!
                            var prognoses = _this.queryResults[PROGNOSES].filter(function (prognosis) { return dimensions.startTime.isBefore(prognosis['Finish']) && dimensions.endTime.isAfter(prognosis['Start']); });
                            if (prognoses.length > 0) {
                                // The calculated capacity.
                                var value_1 = 0;
                                prognoses.forEach(function (prognosis) {
                                    var customers = correction * prognosis['Customers'];
                                    var overlapStart = dimensions.startTime.isSameOrAfter(prognosis['Start']) ? dimensions.startTime.clone() : moment(prognosis['Start']);
                                    var overlapFinish = dimensions.startTime.isSameOrBefore(prognosis['Finish']) ? dimensions.endTime.clone() : moment(prognosis['Finish']);
                                    var timeLength = moment.duration(overlapFinish.diff(overlapStart));
                                    var aht = moment.duration(prognosis['AHT']);
                                    var resources = 0;
                                    var serviceLevel = 0;
                                    while (serviceLevel < sla) {
                                        serviceLevel = ForecastService.getServiceLevel(++resources, customers, timeLength, aht, maxWait);
                                    }
                                    // The number of resources required to meet the SLA is equal to the negative capacity if there are no attendants.
                                    value_1 -= resources * timeLength.asMilliseconds() / dimensions.width.asMilliseconds();
                                });
                                // The calculated capacity without shrinkage.
                                var extraValue = value_1;
                                // The attendants and assignments (breaks) that overlap with the bar.
                                var planItems = _this.queryResults[PLANITEMS].filter(function (planItem) {
                                    return dimensions.startTime.isBefore(planItem['Finish']) && dimensions.endTime.isAfter(planItem['Start']) &&
                                        planItem['Item']['Resource_Oid'] != null;
                                });
                                var attendants = planItems.filter(function (planItem) { return planItem['ItemClass'] === 'Attendant'; }).map(function (planItem) { return planItem['Item']; });
                                var assignments = planItems.filter(function (planItem) { return planItem['ItemClass'] === 'Assignment'; }).map(function (planItem) { return planItem['Item']; });
                                // The shrinkages that overlap with the bar.
                                var dayStart_1 = dimensions.startTime.clone().startOf('day');
                                var barStart_1 = dimensions.startTime.diff(dayStart_1);
                                var barFinish_1 = dimensions.endTime.diff(dayStart_1);
                                var shrinkages = _this.queryResults[SHRINKAGES].map(function (entity) { return entity; }).filter(function (shrinkage) {
                                    return shrinkage.DayOfWeek === PluginViewForecastBase.daysOfWeek[dimensions.startTime.isoWeekday() - 1] &&
                                        shrinkage.Start < barFinish_1 && shrinkage.Finish > barStart_1;
                                });
                                // Find all the points where one of these items start or finish within the bar.
                                var ends_1 = [barStart_1, barFinish_1];
                                planItems.forEach(function (planItem) {
                                    if (dimensions.startTime.isBefore(planItem['Start']) && dimensions.endTime.isAfter(planItem['Start'])) {
                                        ends_1.push(moment(planItem['Start']).diff(dayStart_1));
                                    }
                                    if (dimensions.startTime.isBefore(planItem['Finish']) && dimensions.endTime.isAfter(planItem['Finish'])) {
                                        ends_1.push(moment(planItem['Finish']).diff(dayStart_1));
                                    }
                                });
                                shrinkages.forEach(function (shrinkage) {
                                    if (barStart_1 < shrinkage.Start && barFinish_1 > shrinkage.Start) {
                                        ends_1.push(shrinkage.Start);
                                    }
                                    if (barStart_1 < shrinkage.Finish && barFinish_1 > shrinkage.Finish) {
                                        ends_1.push(shrinkage.Finish);
                                    }
                                });
                                // Put all breakpoints in chronological order.
                                var breakpoints_1 = [];
                                ends_1.sort().forEach(function (end) {
                                    if (breakpoints_1.indexOf(end) === -1) {
                                        breakpoints_1.push(end);
                                    }
                                });
                                var _loop_2 = function (index) {
                                    var fractionalStart = breakpoints_1[index - 1];
                                    var fractionalFinish = breakpoints_1[index];
                                    // Get the width of this bit as a fraction of the whole bar width.
                                    var timeFraction = (fractionalFinish - breakpoints_1[index - 1]) / dimensions.width.asMilliseconds();
                                    // The coverage is the number of agents at work.
                                    var coverage = attendants.filter(function (attendant) {
                                        return moment(attendant['Start']).diff(dayStart_1) <= fractionalStart && moment(attendant['Finish']).diff(dayStart_1) >= fractionalFinish;
                                    }).length;
                                    coverage -= assignments.filter(function (assignment) {
                                        return moment(assignment['Start']).diff(dayStart_1) <= fractionalStart && moment(assignment['Finish']).diff(dayStart_1) >= fractionalFinish;
                                    }).length;
                                    var shrinkage = shrinkages.find(function (shrinkage) {
                                        return shrinkage.Start <= fractionalStart && shrinkage.Finish >= fractionalFinish;
                                    });
                                    extraValue += coverage * timeFraction;
                                    // The coverage needs to be corrected for shrinkage.
                                    value_1 += coverage * timeFraction * (shrinkage ? 1 - shrinkage['Value'] / 100 : 1);
                                };
                                // Calculate capacity coverage per timeframe.
                                for (var index = 1; index < breakpoints_1.length; index++) {
                                    _loop_2(index);
                                }
                                // Get the maximum value, so that the chart is stretched to the max on the y-axis.
                                if (Math.abs(value_1) > maxValue) {
                                    maxValue = Math.abs(value_1);
                                }
                                // One bar for the calculated capacity taking shrinkage into account.
                                var pluginBar = {
                                    startHour: dimensions.startTime.hour(),
                                    startMinute: dimensions.startTime.minute(),
                                    endHour: dimensions.width.asDays() >= 1 ? 24 : dimensions.endTime.hour(),
                                    endMinute: dimensions.width.asDays() >= 1 ? 0 : dimensions.endTime.minute(),
                                    relativeValue: value_1,
                                    value: _this.toMaxTwoDecimals(value_1)
                                };
                                // If there is shrinkage, add the capacity without shrinkage.
                                if (extraValue !== value_1) {
                                    pluginBar.relativeValueExtra = extraValue;
                                    pluginBar.valueExtra = _this.toMaxTwoDecimals(extraValue);
                                    if (Math.abs(extraValue) > maxValue) {
                                        maxValue = Math.abs(extraValue);
                                    }
                                }
                                // Add the bar to the plug-in.
                                gridColumn.pluginBars.push(pluginBar);
                                pluginBars.push(pluginBar);
                            }
                            // Move to the next bar.
                            dimensions.startTime = dimensions.endTime;
                            dimensions.endTime = dimensions.startTime.clone().add(dimensions.width);
                        };
                        do {
                            _loop_1();
                        } while (dimensions.endTime.isSameOrBefore(gridColumn.meta.finish));
                    });
                });
            });
        });
        // All charts need to be scaled equally, so the relative value can only be set at the end, when the maximum value is known.
        pluginBars.forEach(function (pluginBar) {
            pluginBar.relativeValue = Math.round(100 * pluginBar.relativeValue / maxValue);
            if (pluginBar.relativeValueExtra) {
                pluginBar.relativeValueExtra = Math.round(100 * pluginBar.relativeValueExtra / maxValue);
            }
        });
    };
    return PluginViewCapacityComponent;
}(PluginViewForecastBase));
export { PluginViewCapacityComponent };
